<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="loadingAccounts || loadingConversations || loadingMessages"></p-progressBar>

<app-page-shell>

  <app-page-hero
    eyebrow="Workspace"
    title="WhatsApp Chat"
    subtitle="Shared tenant inbox for WASender numbers with queued outbound messages and inbound notifications.">
    <div actions>
      <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" (click)="refreshQuietly()"></p-button>
      <p-button label="New Chat" icon="pi pi-plus" (click)="openNewRecipient()" [disabled]="!canSend || accounts.length === 0"></p-button>
    </div>
  </app-page-hero>

  <section class="wasender-chat-shell">
    <aside class="conversation-panel">
      <div class="conversation-tools">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-whatsapp"></i></p-inputGroupAddon>
          <p-iftaLabel class="flex-1">
            <p-select
              [options]="accounts"
              [(ngModel)]="selectedAccountId"
              optionLabel="displayName"
              optionValue="id"
              appendTo="body"
              [showClear]="true"
              styleClass="w-full"
              (onChange)="onAccountFilterChange()">
            </p-select>
            <label>Number Filter</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
          <p-iftaLabel class="flex-1">
            <input pInputText class="w-full" [(ngModel)]="conversationSearch" (keyup.enter)="loadConversations(false)" />
            <label>Search Contacts</label>
          </p-iftaLabel>
        </p-inputGroup>
        <div class="flex gap-2">
          <p-button label="Search" icon="pi pi-search" size="small" (click)="loadConversations(false)"></p-button>
          <p-button label="Clear" icon="pi pi-times" size="small" severity="secondary" [outlined]="true" (click)="clearSearch()"></p-button>
        </div>
      </div>

      <div class="conversation-list">
        <button
          type="button"
          class="conversation-item"
          *ngFor="let conversation of conversations"
          [class.active]="conversation.id === selectedConversation?.id"
          (click)="selectConversation(conversation)">
          <span class="conversation-avatar">{{ conversationTitle(conversation).charAt(0).toUpperCase() }}</span>
          <span class="conversation-summary">
            <span class="conversation-title">{{ conversationTitle(conversation) }}</span>
            <span class="conversation-subtitle">{{ conversationSubtitle(conversation) }}</span>
            <span class="conversation-last">{{ conversation.lastMessageText || '-' }}</span>
          </span>
          <span class="conversation-meta">
            <small>{{ conversation.lastMessageAt | date:'shortTime' }}</small>
            <span *ngIf="conversation.unreadCount" class="unread-badge">{{ conversation.unreadCount }}</span>
          </span>
        </button>
        <div class="chat-empty-state" *ngIf="!loadingConversations && conversations.length === 0">
          <i class="pi pi-comments"></i>
          <strong>No conversations</strong>
          <span>Start a chat or wait for inbound messages.</span>
        </div>
      </div>
    </aside>

    <main class="message-panel">
      <ng-container *ngIf="selectedConversation as conversation; else noConversationSelected">
        <header class="message-header">
          <div>
            <h2>{{ conversationTitle(conversation) }}</h2>
            <p>{{ conversationSubtitle(conversation) }}</p>
          </div>
          <p-tag [value]="conversation.accountDisplayName || 'WASender'" severity="info"></p-tag>
        </header>

        <div class="message-timeline" #messageScroll>
          <div
            class="message-row"
            *ngFor="let message of messages"
            [class.outbound]="message.direction === 'OUTBOUND'"
            [class.inbound]="message.direction === 'INBOUND'">
            <div class="message-bubble">
              <p class="message-text" *ngIf="message.textContent">{{ message.textContent }}</p>
              <ng-container *ngIf="primaryAttachment(message) as attachment">
                <a class="attachment-preview" [href]="attachment.fileUrl" target="_blank" rel="noopener">
                  <img *ngIf="isImage(attachment)" [src]="attachment.fileUrl" [alt]="attachment.fileName || 'Attachment'" />
                  <span class="attachment-file">
                    <i [class]="attachmentIcon(attachment)"></i>
                    <span>
                      <strong>{{ attachment.fileName || 'Attachment' }}</strong>
                      <small>{{ attachment.mimeType || attachment.mediaKind }} {{ fileSizeLabel(attachment.fileSize) }}</small>
                    </span>
                  </span>
                </a>
              </ng-container>
              <div class="message-meta">
                <span>{{ messageTime(message) | date:'short' }}</span>
                <p-tag *ngIf="message.direction === 'OUTBOUND'" [value]="message.status" [severity]="messageStatusSeverity(message.status)"></p-tag>
              </div>
              <small class="message-error" *ngIf="message.failureReason">{{ message.failureReason }}</small>
            </div>
          </div>
          <div class="chat-empty-state" *ngIf="!loadingMessages && messages.length === 0">
            <i class="pi pi-inbox"></i>
            <strong>No messages yet</strong>
            <span>Send the first message to this contact.</span>
          </div>
        </div>

        <footer class="composer">
          <div class="selected-files" *ngIf="selectedFiles.length">
            <span *ngFor="let file of selectedFiles; let i = index">
              <i class="pi pi-paperclip"></i>
              {{ file.name }}
              <button type="button" (click)="removeSelectedFile(i)" aria-label="Remove file"><i class="pi pi-times"></i></button>
            </span>
          </div>
          <div class="composer-row">
            <p-inputGroup class="composer-message-input">
              <p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon>
              <p-iftaLabel class="flex-1">
                <textarea pTextarea rows="2" class="w-full" [(ngModel)]="composerText"></textarea>
                <label>Message</label>
              </p-iftaLabel>
            </p-inputGroup>
            <input #fileInput type="file" class="hidden-file-input" multiple (change)="onFilesSelected($event)" />
            <p-button icon="pi pi-paperclip" [outlined]="true" severity="secondary" pTooltip="Attach files" (click)="fileInput.click()" [disabled]="!canSend"></p-button>
            <p-button icon="pi pi-send" label="Send" (click)="sendComposerMessage()" [loading]="sending" [disabled]="!canSend || sending || (!composerText.trim() && selectedFiles.length === 0)"></p-button>
          </div>
        </footer>
      </ng-container>

      <ng-template #noConversationSelected>
        <div class="chat-empty-state panel-empty">
          <i class="pi pi-whatsapp"></i>
          <strong>Select a conversation</strong>
          <span>Messages appear here after you choose a contact.</span>
        </div>
      </ng-template>
    </main>
  </section>
</app-page-shell>

<p-dialog
  header="New WhatsApp Chat"
  [(visible)]="newRecipientDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: 'min(620px, 96vw)' }">
  <div class="p-fluid formgrid grid new-chat-form">
    <div class="field col-12">
      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-whatsapp"></i></p-inputGroupAddon>
        <p-iftaLabel class="flex-1">
          <p-select [options]="accounts" [(ngModel)]="newRecipientAccountId" optionLabel="displayName" optionValue="id" appendTo="body" styleClass="w-full"></p-select>
          <label>Sending Number</label>
        </p-iftaLabel>
      </p-inputGroup>
    </div>
    <div class="field col-12 md:col-6">
      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-phone"></i></p-inputGroupAddon>
        <p-iftaLabel class="flex-1">
          <input pInputText [(ngModel)]="newRecipientPhone" placeholder="+9665..." />
          <label>Recipient Phone *</label>
        </p-iftaLabel>
      </p-inputGroup>
    </div>
    <div class="field col-12 md:col-6">
      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
        <p-iftaLabel class="flex-1">
          <input pInputText [(ngModel)]="newRecipientName" />
          <label>Contact Name</label>
        </p-iftaLabel>
      </p-inputGroup>
    </div>
    <div class="field col-12">
      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon>
        <p-iftaLabel class="flex-1">
          <textarea pTextarea rows="4" class="w-full" [(ngModel)]="newRecipientMessage"></textarea>
          <label>Message *</label>
        </p-iftaLabel>
      </p-inputGroup>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="newRecipientDialogVisible = false"></p-button>
    <p-button label="Queue Message" icon="pi pi-send" (click)="sendNewRecipientMessage()" [loading]="sending" [disabled]="!canSend || !newRecipientPhone.trim() || !newRecipientMessage.trim()"></p-button>
  </ng-template>
</p-dialog>
